// Global
import React from 'react';
import { tv } from 'tailwind-variants';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import { Field, LinkField } from '@sitecore-content-sdk/nextjs';

export type ThankYouProps = {
  thankyouTitle?: Field<string>;
  thankyouSuccessMessage?: Field<string>;
  conveyancingCTALink?: LinkField;
  newjourneyCTALink?: LinkField;
};

const contentPanel = tv({
  base: [
    'w-full',
    'bg-color-surface-white',
    'shadow-[0_0_8px_0_rgba(0,0,0,0.1)]',
    'relative',
    'z-10',
  ],
  variants: {
    hasTabs: {
      true: ['rounded-tl-none', 'rounded-tr-none', 'md:rounded-tr-general-border-radius-card'],
      false: ['rounded-general-border-radius-card'],
    },
  },
});

const thankyouTitleStyles = tv({
  base: [
    'text-typography-font-size-h2',
    'font-normal',
    'leading-typography-line-height-h2',
    'font-typography-font-family-header',
  ],
});

const thankyouSuccessMessageStyles = tv({
  base: [
    'text-component-thank-you-success-message',
    'text-typography-font-size-body-large',
    'font-normal',
    'leading-typography-line-height-body-large',
    'font-typography-font-family-body',
    '[&>*]:!m-0',
    'flex',
    'flex-col',
    'gap-spacing-spacing-24',
  ],
});

const navBar = tv({
  base: [
    'w-full',
    'flex',
    'flex-col',
    'lg:flex-row',
    'items-center',
    'justify-between',
    'gap-spacing-spacing-16',
    'px-margin-base',
    'py-padding-less',
    'border-t-component-card-item-border-width',
    'border-solid',
    'border-t-component-Profile-Block-divider',
  ],
});

const ctaAreaBlock = tv({
  slots: {
    ctaLeft: ['flex', 'flex-col', 'lg:flex-row', 'gap-spacing-spacing-16', 'items-center'],
    ctaButtons: ['flex', 'gap-padding-micro', 'p-0', 'flex-wrap', 'justify-center'],
    ctaText: [
      'flex',
      'flex-col',
      'items-center',
      'lg:items-start',
      'justify-center',
      'leading-0',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
      'text-center',
      'lg:text-left',
    ],
    ctaInfo: [
      '[&>*]:!leading-typography-line-height-body-medium',
      '[&>*]:!text-typography-font-size-body-medium',
    ],
    ctaRight: ['flex', 'gap-padding-micro', 'items-center', 'justify-end', 'p-0'],
  },
});

const { ctaLeft, ctaButtons } = ctaAreaBlock();

export const ThankYou: React.FC<ThankYouProps> = (props) => {
  const { thankyouTitle, thankyouSuccessMessage, conveyancingCTALink, newjourneyCTALink } = props;

  return (
    <div className={contentPanel()}>
      <div className="flex flex-col gap-spacing-spacing-24 p-padding-more">
        {thankyouTitle?.value && (
          <PlainTextWrapper
            className={thankyouTitleStyles()}
            field={thankyouTitle}
            tag="h2"
            encode={false}
            {...getTestProps(`thankyou-title`)}
          />
        )}
        {thankyouSuccessMessage?.value && (
          <RichTextWrapper
            className={thankyouSuccessMessageStyles()}
            field={thankyouSuccessMessage}
            tag="div"
            {...getTestProps(`thankyou-success-message`)}
          />
        )}
      </div>
      <div className={navBar()}>
        <div className={ctaLeft()}>
          <div className={ctaButtons()}>
            {conveyancingCTALink ? (
              <LinkWrapper
                field={conveyancingCTALink}
                className="min-w-auto !w-auto"
                ctaComponentClass="default"
                ctaVariant="secondary"
                remixIcon="arrow-right-s-line"
              />
            ) : null}
            {newjourneyCTALink ? (
              <LinkWrapper
                field={newjourneyCTALink}
                className="min-w-auto !w-auto"
                ctaComponentClass="default"
                ctaVariant="primaryOnCanvas"
                remixIcon="arrow-right-s-line"
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
